import { useMemo, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormStateValues, createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorPending } from '@components/icons/form-indicator-pending';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconRemove } from '@components/icons/remove';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Spinner } from '@components/loading-spinner';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { disabledSectionStyle } from '@styles/theme';
import { abbreviateDate } from '@utils/date';

import { IntegrationHeader } from 'components/IntegrationHeader';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { FormFieldConfig } from 'types/forms';

import {
  Cosigner,
  DokobitTemplateResponse,
  SubmitDocumentForm,
  Template,
  useDeleteDokobitDocument,
  useDeleteDokobitForm,
  useDokobitForm,
  useDokobitTemplate,
  usePreviewDocument,
  useSubmitDokobitDocument,
} from './api';

const Form = createForm<SubmitDocumentForm>();

export const DokobitOnboarding = (props: { applicationId: string }) => {
  const { applicationId } = props;
  const { lang, t } = useStore();
  const { showFullscreenModal } = useModal();

  const formQuery = useDokobitForm(applicationId);
  const deleteDocument = useDeleteDokobitDocument(applicationId);
  const showDeleteModal = useDeleteModal();

  if (formQuery.isError) return null;
  if (!formQuery.isSuccess) return <Spinner />;

  const templates = formQuery.data.templates;
  const cosigners = formQuery.data.cosigners;
  const contracts = formQuery.data.contracts;

  return (
    <Stack space={8}>
      <IntegrationHeader
        integration={{
          name: formQuery.data.name,
          title: formQuery.data.title,
          description: formQuery.data.description,
        }}
      />
      {contracts.map((contract) =>
        !contract.inProgress ? (
          <DokobitForm
            templates={templates}
            applicationId={applicationId}
            cosigners={cosigners}
            isSaved
            templateChosen={contract.templateId}
          />
        ) : (
          <Card
            style={deleteDocument.isLoading ? disabledSectionStyle : {}}
            space={4}
            key={contract.id}
          >
            <Stack space={4}>
              <ContractTextLabel
                text={contract.templateTitle}
                label={t('integrations.dokobit.onboarding.contract.document.label')}
              />
              <ContractTextLabel
                label={t('integrations.dokobit.onboarding.contract.datesent.label')}
                text={abbreviateDate(contract.dateSent, lang, 'datetime')}
              />
              <ContractTextLabel
                label={t('integrations.dokobit.onboarding.contract.sentby.label')}
                text={contract.senderName}
              />
              {contract.signers.map((signer) => {
                return (
                  <Stack space={1} key={signer.name}>
                    <Text
                      color="purple100"
                      font="semibold14"
                      text={t('integrations.dokobit.onboarding.contract.signer.label', signer.name)}
                    />
                    <Stack space={1} orientation="horizontal">
                      {signer.status === 'signed' ? (
                        <IconFormIndicatorSuccess />
                      ) : signer.status === 'pending' ? (
                        <IconFormIndicatorPending />
                      ) : (
                        <IconFormIndicatorError />
                      )}
                      <Text
                        color={
                          signer.status === 'signed'
                            ? 'green100'
                            : signer.status === 'pending'
                              ? 'orange100'
                              : 'red100'
                        }
                        text={
                          signer.status === 'signed'
                            ? t('integrations.dokobit.onboarding.contract.signer.signed')
                            : signer.status === 'pending'
                              ? t('integrations.dokobit.onboarding.contract.signer.pending')
                              : t('integrations.dokobit.onboarding.contract.signer.declined')
                        }
                      />
                    </Stack>
                  </Stack>
                );
              })}
              <Stack space={2} orientation="horizontal" justify={'flex-end'}>
                <IconButton
                  icon={<IconVisibilityShown />}
                  tooltip={t('common.preview.label')}
                  onClick={() =>
                    showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: contract.url })
                  }
                />
                <IconButton
                  icon={<IconTrash />}
                  tooltip={t('common.delete.label')}
                  onClick={() =>
                    showDeleteModal({
                      title: t('integrations.dokobit.onboarding.info.delete'),
                      description: t('integrations.dokobit.onboarding.info.delete.description'),
                      buttonText: t('application.onboarding.integration.delete'),
                      hasUserConfirmation: true,
                      onConfirm: () => deleteDocument.mutate(contract.id),
                    })
                  }
                />
              </Stack>
            </Stack>
          </Card>
        ),
      )}
      <DokobitForm templates={templates} applicationId={applicationId} cosigners={cosigners} />
    </Stack>
  );
};

const ContractTextLabel = ({ label, text }: { label: string; text: string }) => {
  return (
    <Stack space={1}>
      <Text font="semibold14" text={label} />
      <Text text={text} />
    </Stack>
  );
};

const DokobitForm = ({
  templates,
  applicationId,
  cosigners,
  templateChosen = null,
  isSaved = false,
}: {
  templates: Template[];
  applicationId: string;
  cosigners: Cosigner[];
  templateChosen?: string | null;
  isSaved?: boolean;
}) => {
  const { t } = useStore();
  const [templateId, setTemplateId] = useState<string | null>(templateChosen);
  const [isNewForm, setIsNewForm] = useState(templateChosen ? false : true);

  const isLoadingPreview = useIsMutating(['dokobit-preview-document']);
  const isSubmitting = useIsMutating(['dokobit-submit-document']);
  const isLoading = isLoadingPreview || isSubmitting;

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: template.id.toString(),
        label: template.title,
      })),
    [templates],
  );

  const defaultTemplate = templateOptions.find(
    (template) => template.value === templateId?.toString(),
  );

  const handleCancel = () => {
    setTemplateId(null);
    setIsNewForm(true);
  };

  if (!isNewForm) {
    return (
      <Card variant="transparent" style={isLoading > 0 ? disabledSectionStyle : {}} space={4}>
        {isLoading > 0 && <Spinner />}
        <Stack space={4}>
          <Select
            options={templateOptions}
            noOptions={() => t('integrations.dokobit.onboarding.form.template.none')}
            onChange={(value) => setTemplateId(value)}
            label={t('integrations.dokobit.onboarding.form.template.label')}
            defaultValue={defaultTemplate ?? undefined}
          />
          {templateId ? (
            <TemplateForm
              templateId={templateId}
              applicationId={applicationId}
              cosigners={cosigners}
              cancel={handleCancel}
              isSaved={isSaved}
            />
          ) : (
            <Stack space={0} orientation="horizontal" justify={'flex-end'}>
              <Button
                text={t('integrations.dokobit.onboarding.form.document.cancel')}
                variant="secondary"
                onClick={handleCancel}
              />
            </Stack>
          )}
        </Stack>
      </Card>
    );
  }
  return (
    <Stack space={4}>
      <Separator />
      <Stack space={0} orientation="horizontal" justify={'flex-end'}>
        <Button
          text={t('integrations.dokobit.onboarding.form.document.add')}
          iconLeft={<IconAddPlus />}
          variant="secondary"
          onClick={() => setIsNewForm(false)}
        />
      </Stack>
    </Stack>
  );
};

type CosignerOptionType = {
  value: string;
  label: string;
};

const TemplateForm = ({
  templateId,
  applicationId,
  cosigners,
  cancel,
  isSaved,
}: {
  templateId: string;
  applicationId: string;
  cosigners: Cosigner[];
  cancel: () => void;
  isSaved: boolean;
}) => {
  const { t } = useStore();

  const templateQuery = useDokobitTemplate(applicationId, templateId);

  if (templateQuery.isError) {
    return <div>{t('integrations.dokobit.onboarding.form.template.error')}</div>;
  }

  if (!templateQuery.isSuccess) return <Spinner />;
  const data = templateQuery.data;

  return (
    <RenderTemplateForm
      config={data}
      templateId={templateId}
      applicationId={applicationId}
      cosigners={cosigners}
      cancel={cancel}
      isSaved={isSaved}
    />
  );
};

const RenderTemplateForm = ({
  config,
  templateId,
  applicationId,
  cosigners,
  cancel,
  isSaved,
}: {
  config: DokobitTemplateResponse;
  templateId: string;
  applicationId: string;
  cosigners: Cosigner[];
  cancel: () => void;
  isSaved: boolean;
}) => {
  const { t } = useStore();

  const preview = usePreviewDocument(applicationId, templateId);
  const submit = useSubmitDokobitDocument(applicationId, templateId);
  const deleteMutation = useDeleteDokobitForm(applicationId, templateId);
  const showDeleteModal = useDeleteModal();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const cosignerOptions = useMemo(() => {
    const cosignersIds = config.cosigners?.map((cosigner) => Number(cosigner.value)) ?? [];
    const filteredCosigners = cosigners.filter((cosigner) => !cosignersIds.includes(cosigner.id));
    return filteredCosigners.map((cosigner) => ({
      value: cosigner.id.toString(),
      label: cosigner.name,
    }));
  }, [cosigners, config.cosigners]);

  const dokobitErrors = submit.error?.response?.data.errors ?? [];
  const values = formStore.useFormValues();

  const prepareValues = (save: boolean, values: FormStateValues): SubmitDocumentForm => {
    const cosigners = values['cosigners'] as CosignerOptionType[];
    return {
      ...values,
      cosigners: cosigners.map((cosigner) => cosigner.value),
      ...(save && { save: true }),
    };
  };

  const submitForm = (save: boolean = false) => {
    const data = prepareValues(save, values);
    return submit.mutateAsync(data);
  };

  return (
    <Stack space={4}>
      {dokobitErrors.length > 0 && (
        <Card variant="red" space={4}>
          <Stack space={2}>
            <Text font="bold14" text={t('integrations.dokobit.onboarding.form.errors.title')} />
            {dokobitErrors.map((error, idx) => (
              <Text font="regular14" text={error} key={idx} />
            ))}
          </Stack>
        </Card>
      )}

      <Form store={formStore} onSubmit={() => submitForm()}>
        <Form.SelectMulti
          id="cosigners"
          options={cosignerOptions}
          label={t('integrations.dokobit.onboarding.form.cosigners.label')}
          placeholder={t('integrations.dokobit.onboarding.form.cosigners.placeholder')}
          noOptions={() => t('integrations.dokobit.onboarding.form.cosigners.nooptions')}
          validators={['required']}
          defaultValue={config.cosigners}
        />

        {config.fields.map((field, idx) => (
          <FormField {...field} key={idx} />
        ))}

        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal">
            {(!isSaved || (isSaved && isDirty)) && (
              <Form.Actions>
                <Form.Reset
                  text={t('application.onboarding.integration.cancel')}
                  variant="secondary"
                  onClick={() => {
                    if (isSaved) {
                      formStore.actions.resetStore();
                    } else {
                      cancel();
                    }
                  }}
                />
              </Form.Actions>
            )}
            <Button
              variant="primary-blue"
              onClick={() => submitForm(true)}
              text={
                !isDirty && isSaved
                  ? t('integrations.dokobit.onboarding.form.save.saved')
                  : t('integrations.dokobit.onboarding.form.save')
              }
              isDisabled={!isDirty}
            />
          </Stack>
          <Stack space={2} orientation="horizontal">
            {isSaved && (
              <IconButton
                tooltip={t('application.onboarding.integration.delete')}
                icon={<IconRemove />}
                onClick={() =>
                  showDeleteModal({
                    title: t('integrations.dokobit.onboarding.info.remove'),
                    description: t('integrations.dokobit.onboarding.info.remove.description'),
                    buttonText: t('application.onboarding.integration.delete'),
                    hideCannotBeUndone: true,
                    onConfirm: () => deleteMutation.mutate(),
                  })
                }
              />
            )}
            <Button
              isDisabled={preview.isLoading}
              tooltip={t('application.onboarding.integration.preview')}
              iconRight={<IconVisibilityShown />}
              variant="secondary"
              onClick={() => {
                preview.mutateAsync(formStore.getState().values);
              }}
            />
            <Form.Actions>
              <Form.Submit text={t('integrations.dokobit.onboarding.form.submit')} />
            </Form.Actions>
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};

function FormField(props: FormFieldConfig) {
  switch (props._type) {
    case 'text':
      return <Form.TextField {...props} id={props.name} />;
    case 'select':
      return <Form.Select {...props} id={props.name} />;
    case 'header':
      return <Text font="bold18" color="purple200" {...props} />;
    case 'separator':
      return <Separator />;
    case 'alert':
      return <Card {...props} />;
    case 'switch':
      return <Form.Switch {...props} id={props.name} />;
    case 'checkbox':
      return <Form.Checkbox {...props} id={props.name} />;
    case 'datepicker':
      return <Form.DatePicker {...props} id={props.name} />;
    default:
      return null;
  }
}
